<script>
	// NPM Modules
	import { createEventDispatcher } from 'svelte';
	// Components
	import Modal from '@/Components/Modal.svelte';
	import TextInput from '@/Components/TextInput.svelte';
	import LoadingButton from '@/Components/LoadingButton.svelte';
	import Icon from '@/Components/Icon.svelte';
	import TextAreaInput from '@/Components/TextAreaInput.svelte';
	// Services
	import { notify } from '@/Services/Notify';

	const dispatch = createEventDispatcher();

	export let market;
	export let open;

	let sending = false;
	let errors = null;
	let selectionsToAdd = [
		{
			id: 1,
			name: '',
			price: '1.00'
		}
	];

	let selectionString = '';
	$: selectionsCount = selectionsToAdd.length;

	function addSelectionToMarket(name = '', price = '1.00') {
		selectionsToAdd = [
			...selectionsToAdd,
			{
				id: selectionsToAdd.length + 1,
				name: name,
				price: price
			}
		];
	}

	function setSelectionToMarket(value) {
		if (value === selectionsToAdd.length) {
			return;
		}

		if (value > selectionsToAdd.length) {
			let numberToCreate = value - selectionsToAdd.length;
			for (let i = 0; i < numberToCreate; i++) {
				selectionsToAdd.push({
					id: selectionsToAdd.length + 1,
					name: '',
					price: '1.00'
				});
			}
			selectionsToAdd = selectionsToAdd;
		}
	}

	function removeSelectionFromMarket(key) {
		selectionsToAdd.splice(key, 1);

		let count = 1;
		for (let index in selectionsToAdd) {
			selectionsToAdd[index].id = count;
			count = count + 1;
		}

		selectionsToAdd = selectionsToAdd; //Trigger reactivity
	}

	function createSelectionsFromString(stringSent) {
		// eslint-disable-next-line no-useless-escape
		const regexp = /(.*)[\s]([\d\.]*)(?:[\r\n]|$)/g;
		const selectionArray = [...stringSent.matchAll(regexp)];
		let generatedSelectionsToAdd = [];
		for (let index in selectionArray) {
			generatedSelectionsToAdd.push({
				name: selectionArray[index][1],
				price: selectionArray[index][2]
			});
		}

		let canAddToExisting = false;
		if (selectionsToAdd.length > 0) {
			canAddToExisting = true;
		}
		for (const selection of generatedSelectionsToAdd) {
			if (canAddToExisting) {
				let added = false;
				for (let index in selectionsToAdd) {
					if (selectionsToAdd[index].name == '' && selectionsToAdd[index].price == '1.00') {
						selectionsToAdd[index].name = selection.name;
						selectionsToAdd[index].price = selection.price;
						added = true;
						break;
					}
				}
				if (!added) {
					canAddToExisting = false;
				} else {
					continue;
				}
			}
			addSelectionToMarket(selection.name, selection.price);
		}
	}

	function createMarketSelections() {
		if (selectionsToAdd.length === 0) {
			notify({
				type: 'danger',
				title: 'Error!',
				message: 'You need to add a selection to create a new selection!',
				timeout: 3
			});
			return;
		}
		errors = {};
		let failedValidation = false;
		for (const selection in selectionsToAdd) {
			if (selectionsToAdd[selection].price < 1) {
				errors['sport_selections.' + selection + '.price'] = ['Odds must be greater than or equal too $1.00'];
				failedValidation = true;
			}
			if (selectionsToAdd[selection].name === '') {
				errors['sport_selections.' + selection + '.name'] = ['Selection requires a name'];
				failedValidation = true;
			}
			for (const { id, name } of selectionsToAdd) {
				if (selectionsToAdd[selection].name == name && selectionsToAdd[selection].id != id) {
					errors['sport_selections.' + selection + '.name'] = [
						'Selections require a unique name, this name is in use.'
					];
					failedValidation = true;
				}
			}
		}
		if (failedValidation) {
			return false;
		}
		let transformedSelections = [];
		for (const selection in selectionsToAdd) {
			transformedSelections.push({
				name: selectionsToAdd[selection].name,
				price: selectionsToAdd[selection].price * 100
			});
		}
		sending = true;
		market
			.addSelections(transformedSelections)
			.then(() => {
				notify({
					type: 'success',
					title: 'Market selections created',
					message: 'Market selections successfully created!',
					timeout: 3
				});
				dispatch('created');
				open = false;
				selectionsToAdd = [
					{
						id: 1,
						name: '',
						price: '1.00'
					}
				];
			})
			.catch((e) => {
				errors = e;
				notify({
					type: 'danger',
					title: 'Error!',
					message: (e.error && e.error[0]) || 'Problem creating market selections',
					timeout: 3
				});
			})
			.finally(() => {
				sending = false;
			});
	}
</script>

{#if open}
	<Modal on:close={() => (open = false)} className="md:w-2/3 lg:w-1/2 mx-2 md:mx-auto">
		<h2 slot="header" class="pt-2 text-xl font-bold text-center">Create selections for {market.name}</h2>
		<div class="flex flex-col w-full px-4 pt-6 text-left">
			<div class="mt-4">
				<div class="flex flex-row items-center">
					<div class="mr-4">Selections to add:</div>
					<div class="flex flex-row border border-gray-400">
						<input
							on:change={(event) => setSelectionToMarket(event.target.value)}
							on:keyup={(event) => {
								if (event.keyCode === 13) {
									setSelectionToMarket(event.target.value);
								}
							}}
							disabled={sending}
							type="number"
							class="w-10 text-xs text-center border-gray-400 focus:outline-none"
							value={selectionsCount}
						/>
						<button
							disabled={sending}
							on:click|stopPropagation={() => addSelectionToMarket()}
							class="p-1 font-semibold text-white bg-gray-500 cursor-pointer focus:outline-none hover:bg-gray-400"
						>
							<span class="m-auto">+</span>
						</button>
					</div>
				</div>
				<div class="flex flex-row items-center justify-center">
					<TextAreaInput
						rows="4"
						disabled={sending}
						name="pasteFromString"
						className="py-4 w-1/2 text-left"
						placeholder="Paste selection text here."
						bind:value={selectionString}
					/>
					<LoadingButton
						on:click={() => createSelectionsFromString(selectionString)}
						loading={false}
						disabled={sending}
						className="ml-2 my-4 btn btn-primary"
					>
						Generate selections
					</LoadingButton>
				</div>
			</div>
			<div class="flex justify-center">
				<table class="text-left mt-2 w-2/3">
					{#if selectionsToAdd.length > 0}
						<tr>
							<th class="w-pw" />
							<th class="w-3/4">Selection Name</th>
							<th class="w-1/4">Odds ($)</th>
							<th class="w-px" />
						</tr>
					{/if}
					{#each selectionsToAdd as selection, key (key)}
						<tr>
							<td>
								<span class="mr-2">{selection.id}</span>
							</td>
							<td>
								<TextInput
									name={`selection.${selection.id}.name`}
									required={true}
									disabled={sending}
									className="w-full text-left"
									tabindex="-1"
									errors={errors && errors['sport_selections.' + key + '.name']}
									placeholder="Enter selection name"
									bind:value={selection.name}
								/>
							</td>
							<td>
								<div class="w-full text-left">
									<input
										name={`selection.${selection.id}.price`}
										required={true}
										disabled={sending}
										type="number"
										class="form-input focus:outline-none"
										class:border-orange-400={parseFloat(selection.price) === 1.0}
										class:error={errors && errors['sport_selections.' + key + '.price']}
										min="1.00"
										step="0.01"
										tabindex="0"
										placeholder="Enter selection odds"
										on:change={(event) => (selection.price = parseFloat(event.target.value).toFixed(2))}
										on:keyup={(event) => {
											if (event.keyCode === 13) {
												selection.price = parseFloat(event.target.value).toFixed(2);
											}
										}}
										value={selection.price}
									/>

									{#if errors && errors['sport_selections.' + key + '.price']}
										<div class="form-error">{errors['sport_selections.' + key + '.price'][0]}</div>
									{/if}
								</div>
							</td>
							<td>
								<button
									disabled={sending}
									on:click={() => removeSelectionFromMarket(key)}
									tabindex="-1"
									title="Remove selection from market"
									class="p-1 ml-1 text-white rounded bg-danger hover:bg-grey focus:outline-none"
								>
									<Icon name="bin" />
								</button>
							</td>
						</tr>
					{:else}
						<tr class="text-center">
							<td colspan="2">No selections added</td>
						</tr>
					{/each}
				</table>
			</div>

			{#if errors && errors.sport_selections}
				<div class="form-error">{errors.sport_selections[0]}</div>
			{/if}
			<LoadingButton on:click={() => createMarketSelections()} loading={sending} className="my-4 btn btn-primary">
				{sending ? 'Creating selections' : 'Create selections'}
			</LoadingButton>
			<button class="mb-4 btn btn-muted" on:click={() => (open = false)}>Cancel</button>
		</div>
	</Modal>
{/if}
